import type SessionService from '@efdigital/ll-addon-auth/services/session';
import {
  type CoachingSession,
  type CoachProfile,
} from '@efdigital/ll-addon-data';
import { queryCoachProfilesByUserIds } from '@efdigital/ll-xceed-v2/data/builders/coaching/coach-profiles';
import { queryCoachingSessionsByCoachingPlan } from '@efdigital/ll-xceed-v2/data/builders/coaching/coaching-sessions';
import type CoacheeService from '@efdigital/ll-xceed-v2/services/coachee';
import {
  COACHEE_PENDING_CONFIRMATION_KEYS,
  COACHEE_UPCOMING_KEYS,
} from '@efdigital/ll-xceed-v2/utils/coaching/session-current-categories';
import Route from '@ember/routing/route';
import { service } from '@ember/service';
import type { Store } from '@warp-drive/core';

import type { CoachingCoacheeCohortModel } from '../../cohort';

export type CoachingCoacheeCohortSessionsIndexModel =
  CoachingCoacheeCohortModel & {
    upcoming: CoachingSession[];
    coaches: CoachProfile[];
  };

function sortByStartTimeAsc(sessions: CoachingSession[]): CoachingSession[] {
  return [...sessions].sort(
    (left, right) => Date.parse(left.startTime) - Date.parse(right.startTime)
  );
}

export default class CoachingCoacheeCohortSessionsIndexRoute extends Route<CoachingCoacheeCohortSessionsIndexModel> {
  @service declare coachee: CoacheeService;
  @service declare store: Store;
  @service declare session: SessionService;

  async model(): Promise<CoachingCoacheeCohortSessionsIndexModel> {
    const cohortModel = this.modelFor(
      'coaching.coachee.cohort'
    ) as CoachingCoacheeCohortModel;
    const coachingPlanId = cohortModel.coachingPlan!.id!;

    const [upcomingSessions, pendingSessions] = await Promise.all([
      this.store.request(
        queryCoachingSessionsByCoachingPlan(
          coachingPlanId,
          COACHEE_UPCOMING_KEYS
        )
      ),
      this.store.request(
        queryCoachingSessionsByCoachingPlan(
          coachingPlanId,
          COACHEE_PENDING_CONFIRMATION_KEYS
        )
      ),
    ]);

    const upcoming = sortByStartTimeAsc([
      ...pendingSessions.content.data,
      ...upcomingSessions.content.data,
    ]);

    const coachUserIds = Array.from(
      new Set(upcoming.map((session) => session.coachUserId))
    );
    const coaches = await this.store.request(
      queryCoachProfilesByUserIds(coachUserIds)
    );

    return {
      ...cohortModel,
      upcoming,
      coaches: coaches.content.data,
    };
  }
}
